import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ---------------- Creator Applications (admin) ---------------- */
export default function CreatorApplications() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const refresh = async () => {
    const { data } = await api.get("/admin/creator/applications", { params: filter ? { status: filter } : {} });
    setItems(data || []);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (a, decision) => {
    const reason = decision === "rejected"
      ? prompt("Reason for rejection (optional, will email the applicant):") || null
      : prompt("Welcome note (optional, will email the applicant):") || null;
    setBusy(a.application_id);
    try {
      await api.post(`/admin/creator/applications/${a.application_id}/${decision === "approved" ? "approve" : "reject"}`, { reason });
      toast.success(`Application ${decision}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-creator-applications">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <User2 className="w-5 h-5 text-[#f6c34a]" /> Creator Applications
        </h2>
        <div className="flex gap-1 bg-[#0f0f0f] border border-white/10 rounded p-1 text-xs">
          {["pending", "approved", "rejected", ""].map((f) => (
            <button
              key={f || "all"}
              data-testid={`apps-filter-${f || "all"}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded transition ${filter === f ? "bg-[#E50914] text-white font-semibold" : "text-gray-400 hover:text-white"}`}
            >{f ? f[0].toUpperCase() + f.slice(1) : "All"}</button>
          ))}
        </div>
      </div>
      {items.length === 0 && (
        <div className="text-center text-gray-500 py-16 border border-dashed border-white/10 rounded-lg">
          No {filter || "applications"} right now.
        </div>
      )}
      <div className="space-y-3">
        {items.map((a) => (
          <div
            key={a.application_id}
            data-testid={`application-row-${a.application_id}`}
            className="rounded-lg border border-white/10 bg-[#0f0f0f] p-5"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-bold">{a.display_name || a.applicant_name}</span>
                  <span className="text-xs text-gray-500">· {a.applicant_email}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded border ${
                    a.role === "channel_owner"
                      ? "text-cyan-300 border-cyan-500/40 bg-cyan-500/10"
                      : "text-violet-300 border-violet-500/40 bg-violet-500/10"
                  }`}>{a.role.replace("_", " ")}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-bold ${
                    a.status === "approved" ? "text-green-400" :
                    a.status === "rejected" ? "text-red-400" : "text-amber-400"
                  }`}>{a.status}</span>
                </div>
                {a.bio && <p className="text-sm text-gray-300 mt-2 leading-relaxed">{a.bio}</p>}
                {a.motivation && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Motivation</div>
                    <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{a.motivation}</p>
                  </div>
                )}
                {a.portfolio_url && (
                  <a href={a.portfolio_url} target="_blank" rel="noreferrer" className="text-xs text-[#f6c34a] hover:text-white mt-3 inline-block">
                    Portfolio: {a.portfolio_url}
                  </a>
                )}
                {a.sample_links && (
                  <pre className="text-xs text-gray-400 mt-2 whitespace-pre-wrap font-mono bg-black/40 p-2 rounded">{a.sample_links}</pre>
                )}
                <div className="text-[11px] text-gray-500 mt-3">
                  Submitted {new Date(a.created_at).toLocaleString()}
                  {a.decided_at && ` · decided ${new Date(a.decided_at).toLocaleString()}`}
                  {a.reason && ` · reason: ${a.reason}`}
                </div>
              </div>
              {a.status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`approve-${a.application_id}`}
                    disabled={busy === a.application_id}
                    onClick={() => decide(a, "approved")}
                    className="bg-green-600 hover:bg-green-700 transition px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"
                  >
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button
                    data-testid={`reject-${a.application_id}`}
                    disabled={busy === a.application_id}
                    onClick={() => decide(a, "rejected")}
                    className="bg-white/10 hover:bg-white/20 border border-white/15 transition px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"
                  >
                    <Ban className="w-4 h-4" /> Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
